// Line-count audit (2026-05-04): 238 lines.
import {
  summarizeBindingContextForAi,
  type AiBindingContextSummary,
  type AiBindingSourceIdentitySummary,
  type AiBindingWriteTargetSummary,
} from '@/services/ai/BindingContextSummary';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';
import type { Component, Page, Project } from '@/types';

export {
  getAgentThreadKey,
  getUserAgentThreadKey,
  shouldPreferUserAgentContinuation,
} from './agentContinuationUtils';
export {
  filterDimensionChanges,
  isDimensionPropertyChange,
} from './agentDimensionUtils';

export interface AgentContextEditorState {
  currentPageId: string | null | undefined;
  currentPageComponents: Component[];
  pageComponents: Map<string, Component[]>;
}

interface QueryDefinitionLike {
  schemaId?: string;
}

interface CompositeQuerySourceLike {
  schemaId?: string;
}

interface CompositeQueryDefinitionLike {
  sources?: CompositeQuerySourceLike[];
}

interface DataSourceLike {
  queryDefinition?: QueryDefinitionLike;
  compositeQueryDefinition?: CompositeQueryDefinitionLike;
}

const WRITE_OPERATIONS: ComponentDataWriteOperation[] = ['create', 'update', 'delete'];

function getDataSource(component: Component): DataSourceLike | null {
  const properties = component.properties;
  if (typeof properties !== 'object' || properties === null || !('dataSource' in properties)) {
    return null;
  }

  const dataSource = properties.dataSource;
  if (typeof dataSource !== 'object' || dataSource === null) {
    return null;
  }

  const queryDefinition = 'queryDefinition' in dataSource && typeof dataSource.queryDefinition === 'object'
    && dataSource.queryDefinition !== null ? dataSource.queryDefinition : undefined;
  const compositeQueryDefinition = 'compositeQueryDefinition' in dataSource && typeof dataSource.compositeQueryDefinition === 'object'
    && dataSource.compositeQueryDefinition !== null ? dataSource.compositeQueryDefinition : undefined;

  return {
    queryDefinition,
    compositeQueryDefinition,
  };
}


function getLegacySchemaIds(dataSource: DataSourceLike): string[] {
  const schemaIds = new Set<string>();
  if (dataSource.queryDefinition?.schemaId) {
    schemaIds.add(dataSource.queryDefinition.schemaId);
  }

  for (const source of dataSource.compositeQueryDefinition?.sources || []) {
    if (source?.schemaId) {
      schemaIds.add(source.schemaId);
    }
  }

  return Array.from(schemaIds).slice(0, 5);
}

function formatSourceIdentity(source: AiBindingSourceIdentitySummary): string {
  if (source.kind === 'schema') {
    return [
      'schema',
      `dataOwnerOrganizationId=${source.dataOwnerOrganizationId}`,
      `ownerType=${source.ownerType}`,
      `ownerId=${source.ownerId}`,
      `groupId=${source.groupId}`,
      `schemaId=${source.schemaId}`,
    ].join(' ');
  }

  if (source.kind === 'static') {
    return `static adapterId=${source.adapterId}`;
  }

  if (source.kind === 'unknown') {
    return 'unknown';
  }

  return [
    source.kind,
    `ownerType=${source.ownerType}`,
    `ownerId=${source.ownerId}`,
    `viewOrganizationId=${source.viewOrganizationId}`,
    `viewId=${source.viewId}`,
  ].join(' ');
}

function formatFieldMapping(summary: AiBindingContextSummary): string {
  const mappingEntries = Object.entries(summary.fieldMapping)
    .slice(0, 8)
    .map(([componentField, sourceField]) =>
      `${componentField}->${sourceField || 'unmapped'}`,
    );

  return mappingEntries.length > 0 ? mappingEntries.join(', ') : 'none';
}

function formatReadiness(
  readiness: Partial<Record<ComponentDataWriteOperation, string>> | undefined,
): string | null {
  if (readiness === undefined) {
    return null;
  }

  const readinessEntries = WRITE_OPERATIONS.flatMap((operation) => {
    const value = readiness[operation];
    return value === undefined ? [] : [`${operation}=${value}`];
  });

  return readinessEntries.length > 0 ? readinessEntries.join(', ') : null;
}

function formatWriteTarget(writeTarget: AiBindingWriteTargetSummary): string {
  if (!writeTarget.present) {
    return 'absent';
  }

  if ('kind' in writeTarget && writeTarget.kind === 'federated-view-source') {
    const readiness = formatReadiness(writeTarget.readiness);
    const readinessSuffix = readiness === null ? '' : ` readiness ${readiness}`;
    return [
      'present federated-view-source',
      `targetSourceId=${writeTarget.targetSourceId}`,
      `view=${formatSourceIdentity(writeTarget.view)}`,
      `source=${formatSourceIdentity(writeTarget.source)}`,
    ].join(' ') + readinessSuffix;
  }

  if ('target' in writeTarget) {
    const readiness = formatReadiness(writeTarget.readiness);
    const readinessSuffix = readiness === null ? '' : ` readiness ${readiness}`;
    return `present ${formatSourceIdentity(writeTarget.target)}${readinessSuffix}`;
  }

  return 'absent';
}


function formatLabels(summary: AiBindingContextSummary): string | null {
  const labels: string[] = [];
  if (summary.sourceLabels) {
    labels.push(
      `source=${summary.sourceLabels.schemaDisplayName}`,
      `group=${summary.sourceLabels.groupName}`,
    );
  }

  if (summary.viewLabels) {
    labels.push(`view=${summary.viewLabels.name}`);
    if (summary.viewLabels.ownerName) {
      labels.push(`viewOwner=${summary.viewLabels.ownerName}`);
    }
  }

  return labels.length > 0 ? labels.join(', ') : null;
}

function formatRelatedComponents(summary: AiBindingContextSummary): string | null {
  if (summary.relatedComponents.length === 0) {
    return null;
  }

  return summary.relatedComponents
    .slice(0, 5)
    .map((related) => `${related.id}(${related.type})`)
    .join(', ');
}

function formatBindingContextSummary(summary: AiBindingContextSummary): string {
  const parts = [
    `Binding context: ${summary.status}`,
    `source ${formatSourceIdentity(summary.source)}`,
  ];

  if (summary.reconnectReason) {
    parts.push(`reason ${summary.reconnectReason}`);
  }

  const labels = formatLabels(summary);
  if (labels) {
    parts.push(`labels ${labels}`);
  }

  parts.push(`field mappings ${formatFieldMapping(summary)}`);
  parts.push(`write target ${formatWriteTarget(summary.writeTarget)}`);

  const related = formatRelatedComponents(summary);
  if (related) {
    parts.push(`related components ${related}`);
  }

  return parts.join('; ');
}

function buildSelectedComponentDataContext(
  component: Component,
  relatedComponents: Component[],
): string | null {
  if (component.properties.dataBinding !== undefined) {
    return formatBindingContextSummary(
      summarizeBindingContextForAi(component, { relatedComponents }),
    );
  }

  const dataSource = getDataSource(component);
  if (dataSource !== null) {
    const schemaIds = getLegacySchemaIds(dataSource);
    return schemaIds.length > 0
      ? `Legacy data source schema IDs: ${schemaIds.join(', ')}`
      : 'Legacy data source schema IDs: none';
  }

  return formatBindingContextSummary(summarizeBindingContextForAi(component));
}

function getComponentNames(components: Component[]): string[] {
  return components
    .map((component) => component.name?.trim())
    .filter((name): name is string => Boolean(name))
    .filter((name, index, names) => names.indexOf(name) === index)
    .slice(0, 5);
}

export function buildAgentContext(
  storePages: Page[],
  isCanvas: boolean,
  component: Component | null | undefined,
  project: Project | null | undefined,
  editorState: AgentContextEditorState
): string {
  const parts: string[] = [];
  const allPages = storePages ?? [];

  const summarizePage = (componentsForPage: Component[]) => {
    const typeCounts = componentsForPage.reduce<Record<string, number>>((accumulator, current) => {
      accumulator[current.type] = (accumulator[current.type] || 0) + 1;
      return accumulator;
    }, {});

    const topTypes = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([type, count]) => `${type}(${count})`);

    const dataBoundComponents = componentsForPage.filter((current) => {
      const dataSource = getDataSource(current);
      return Boolean(
        dataSource?.queryDefinition?.schemaId ||
        (Array.isArray(dataSource?.compositeQueryDefinition?.sources) &&
          dataSource.compositeQueryDefinition.sources.length > 0)
      );
    });

    const schemaIds = new Set<string>();
    const sampleNames = getComponentNames(componentsForPage);

    for (const current of dataBoundComponents) {
      const dataSource = getDataSource(current);
      if (dataSource) {
        for (const schemaId of getLegacySchemaIds(dataSource)) {
          schemaIds.add(schemaId);
        }
      }
    }

    return {
      topTypes,
      dataBoundCount: dataBoundComponents.length,
      schemaIds: Array.from(schemaIds).slice(0, 3),
      sampleNames,
    };
  };

  const pageSummaries = allPages.map((page) => {
    const pageComponents = editorState.pageComponents.get(page.id) || [];
    const effectiveComponents = page.id === editorState.currentPageId
      ? editorState.currentPageComponents
      : pageComponents;
    const details = summarizePage(effectiveComponents);

    return {
      id: page.id,
      name: page.name,
      count: effectiveComponents.length,
      details,
    };
  });

  const totalProjectComponents = pageSummaries.reduce((sum, page) => sum + page.count, 0);
  const currentPage = allPages.find((page) => page.id === editorState.currentPageId);
  const populatedOtherPages = pageSummaries
    .filter((page) => page.id !== editorState.currentPageId && page.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  if (project?.id) {
    parts.push(`Project ID: ${project.id}`);
  }

  if (project?.name) {
    parts.push(`Project name: ${project.name}`);
  }

  if (project?.description) {
    parts.push(`Project description: ${project.description}`);
  }

  if (project?.tags?.length) {
    parts.push(`Project tags: ${project.tags.slice(0, 8).join(', ')}`);
  }

  if (editorState.currentPageId) {
    parts.push(`Current page: ${editorState.currentPageId}`);
  }

  if (currentPage?.name) {
    parts.push(`Current page name: ${currentPage.name}`);
  }

  if (storePages?.length) {
    parts.push(`Project has ${storePages.length} page${storePages.length === 1 ? '' : 's'}`);
  }

  parts.push(
    `Current page has ${editorState.currentPageComponents.length} component${editorState.currentPageComponents.length === 1 ? '' : 's'}`
  );
  parts.push(`Project-wide total components: ${totalProjectComponents}`);

  if (currentPage) {
    const currentPageSummary = pageSummaries.find((page) => page.id === currentPage.id);
    if (currentPageSummary?.details.topTypes.length) {
      parts.push(`Current page structure: ${currentPageSummary.details.topTypes.join(', ')}`);
    }
    if (currentPageSummary?.details.dataBoundCount) {
      parts.push(`Current page data-bound components: ${currentPageSummary.details.dataBoundCount}`);
    }
    if (currentPageSummary?.details.sampleNames.length) {
      parts.push(`Current page sample components: ${currentPageSummary.details.sampleNames.join(', ')}`);
    }
  }

  if (populatedOtherPages.length > 0) {
    parts.push(`Other populated pages: ${populatedOtherPages.map((page) => `${page.name} (${page.count})`).join(', ')}`);

    for (const page of populatedOtherPages) {
      const detailBits: string[] = [];
      if (page.details.topTypes.length) {
        detailBits.push(`structure ${page.details.topTypes.join(', ')}`);
      }
      if (page.details.dataBoundCount) {
        detailBits.push(`data-bound ${page.details.dataBoundCount}`);
      }
      if (page.details.schemaIds.length) {
        detailBits.push(`schemas ${page.details.schemaIds.join(', ')}`);
      }
      if (page.details.sampleNames.length) {
        detailBits.push(`components ${page.details.sampleNames.join(', ')}`);
      }
      if (detailBits.length) {
        parts.push(`Page detail: ${page.name} -> ${detailBits.join(' | ')}`);
      }
    }
  }

  parts.push(`Chat scope: ${isCanvas ? 'canvas' : 'component'}`);

  if (component) {
    parts.push(`Selected component: ${component.type} "${component.name || component.id}"`);

    const dataComponentTypes = new Set([
      'chart',
      'bar-chart',
      'line-chart',
      'pie-chart',
      'scatter-chart',
      'area-chart',
      'heatmap-chart',
      'treemap-chart',
      'gauge-chart',
      'vertical-gauge-chart',
      'map-chart',
      'data-card',
      'data-text',
      'fermentation-tank-card',
      'table',
      'advanced-table',
    ]);

    if (dataComponentTypes.has(component.type)) {
      const dataContext = buildSelectedComponentDataContext(
        component,
        editorState.currentPageComponents,
      );
      if (dataContext) {
        parts.push(dataContext);
      }
    }
  }

  parts.push(`Canvas has ${editorState.currentPageComponents.length} components`);
  return parts.join('\n');
}
